import { Box } from '@mui/material'
import React from 'react'
import Button from '../components/Button'
import SimpleTable, { SimpleTableObject } from '../components/SimpleTable'

type Item = {
	id: string
	__type: string
	rows?: Item[]
	__dict?: Record<string, string>
	emptyRowsFallBackContextType?: string
	[key: string]: unknown
	title?: string
	parentId?: string
}
type CreatePayload = { action: 'create'; parentId?: string; __type: string }
type EditPayload = Item & { action: 'edit'; __type: string }
type DeletePayload = Item & { action: 'delete'; __type: string }
type MovePayload = {
	action: 'move'
	itemId: string
	parentId?: string
	__type: string
	direction: 1 | -1
}
type AttachPayload = { action: 'attach'; parentId?: string; __type: string }
export type ActionPayload = CreatePayload | EditPayload | DeletePayload | AttachPayload
type AttachFlags = {
	category?: boolean
	product?: boolean
	optionGroup?: boolean
	option?: boolean
}

const EditableTable = ({
	objects,
	dict,
	onAction,
	onConfirm,
	onMove,
	showAttachFlagsFor,
	hideHead,
	expandSignal,
	collapseSignal,
	onEmptyCreate,
	disableExpandIcons,
	renderActionsExtra,
	hideEdit,
	hideDelete,
	focusId,
	focusSignal,
	highlightId,
	highlightSignal,
	disablePagination,
	parentId,
	emptyRowsFallBackContextType,
}: {
	objects: Item[]
	dict: Record<string, string>
	onAction?: (obj: ActionPayload) => Promise<void> | void
	onConfirm?: (obj: Item) => Promise<void>
	onMove?: (obj: MovePayload) => Promise<void> | void
	showAttachFlagsFor?: AttachFlags
	hideHead?: boolean
	expandSignal?: number
	collapseSignal?: number
	onEmptyCreate?: () => void
	disableExpandIcons?: boolean
	renderActionsExtra?: (id: string) => JSX.Element | null
	hideEdit?: boolean
	hideDelete?: boolean
	focusId?: string
	focusSignal?: number
	highlightId?: string
	highlightSignal?: number
	disablePagination?: boolean
	parentId?: string
	emptyRowsFallBackContextType: string
}): JSX.Element => {
	const activeKeys = React.useMemo(() => {
		return Object.keys(dict)
	}, [dict])

	const findObjectById = React.useCallback(
		(list: Item[], targetId: string): Item | undefined => {
			for (const entry of list) {
				if (String(entry.id) === String(targetId)) return entry
				if (entry.rows?.length) {
					const found = findObjectById(entry.rows, targetId)
					if (found) return found
				}
			}
			return undefined
		},
		[]
	)

	const findRowWithSiblings = React.useCallback(
		(
			list: Item[],
			targetId: string
		): { row: Item; siblings: Item[] } | null => {
			for (const entry of list) {
				if (String(entry.id) === String(targetId)) {
					return { row: entry, siblings: list as Item[] }
				}
				if (entry.rows?.length) {
					const found = findRowWithSiblings(entry.rows, targetId)
					if (found) return found
				}
			}
			return null
		},
		[]
	)

	const handleEdit = React.useCallback(
		(id) => {
			if (!onAction) return
			const obj = findObjectById(objects, id)
			if (!obj) return
			onAction({ ...obj, action: 'edit', __type: obj.__type })
		},
		[findObjectById, objects, onAction]
	)

	const handleDelete = React.useCallback(
		(id) => {
			if (!onAction) return
			const obj = findObjectById(objects, id)
			if (!obj) return
			onAction({ ...obj, action: 'delete', __type: obj.__type })
		},
		[findObjectById, objects, onAction]
	)

	const handleConfirm = React.useCallback(
		(id) => {
			const obj = objects.find((c) => c.id === id)
			if (!obj) return
			onConfirm(obj)
		},
		[objects, onConfirm]
	)

	const handleMove = React.useCallback(
		(payload: MovePayload) => {
			if (!onMove) return
			onMove(payload)
		},
		[onMove]
	)

	const renderActions = React.useCallback(
		(id) => (
			<Box display="flex" flexDirection="row" alignItems="center" gap="0.35rem" flexWrap="nowrap">
				{renderActionsExtra ? renderActionsExtra(String(id)) : null}
				{onMove
					? (() => {
						const rowsWithSiblings = findRowWithSiblings(objects, String(id))
						if (!rowsWithSiblings) return null
						const index = rowsWithSiblings.siblings.findIndex(
							(row) => String(row.id) === String(id)
						)
						if (index === -1) return null
						const itemType = rowsWithSiblings.row.__type
						const parentId = rowsWithSiblings.row.parentId
							return (
								<Box display="flex" flexDirection="row" alignItems="center" gap="0.35rem">
									{index === 0 ? null : (
										<Button
											variant="outlined"
											id={id}
											iconName="ArrowUpward"
											onClick={() =>
												handleMove({
													action: 'move',
													itemId: String(rowsWithSiblings.row.id),
													parentId,
													__type: itemType,
													direction: -1,
												})
											}
										/>
									)}
									{index === rowsWithSiblings.siblings.length - 1 ? null : (
										<Button
											variant="outlined"
											id={id}
											iconName="ArrowDownward"
											onClick={() =>
												handleMove({
													action: 'move',
													itemId: String(rowsWithSiblings.row.id),
													parentId,
													__type: itemType,
													direction: 1,
												})
											}
										/>
									)}
								</Box>
							)
					  })()
					: null}
				{onAction && !hideEdit ? (
					<Button variant="outlined" onClick={handleEdit} id={id} iconName="EditRounded" />
				) : null}
				{onAction && !hideDelete ? (
					<Button variant="outlined" onClick={handleDelete} id={id} iconName="Delete" />
				) : null}
				{onConfirm ? (
					<Button variant="outlined" onClick={handleConfirm} id={id} iconName="Check" />
				) : null}
			</Box>
		),
		[
			findRowWithSiblings,
			handleConfirm,
			handleDelete,
			handleEdit,
			handleMove,
			objects,
			onConfirm,
			onMove,
			renderActionsExtra,
			onAction,
			hideDelete,
		]
	)

	const renderHeadActions = React.useCallback(
		({ parentId, __type }: { parentId?: string; __type: string }) => {
			const attachContext = __type
			const shouldAttach =
				onAction &&
				showAttachFlagsFor &&
				((attachContext === 'category' && showAttachFlagsFor.category) ||
					(attachContext === 'product' && showAttachFlagsFor.product) ||
					(attachContext === 'optionGroup' && showAttachFlagsFor.optionGroup) ||
					(attachContext === 'option' && showAttachFlagsFor.option))
			const attachButton = shouldAttach ? (
				<Button
					variant="outlined"
					onClick={() => {
						onAction({ action: 'attach', parentId, __type })
					}}
					iconName="PlaylistAdd"
					id={parentId}
				/>
			) : null
			const addButton = onAction ? (
				<Button
					variant="outlined"
					onClick={() => {
						onAction({ action: 'create', parentId, __type })
					}}
					iconName="Add"
					id={parentId}
				/>
			) : null
			if (!addButton && !attachButton) return null
			return (
				<Box display="flex" justifyContent="flex-end" width="100%" gap="0.35rem">
					{attachButton}
					{addButton}
				</Box>
			)
		},
		[onAction, showAttachFlagsFor]
	)

	return (
		<>
			<SimpleTable
				objects={objects}
				activeKeys={activeKeys}
				dict={dict}
				renderActions={onAction || onConfirm || onMove ? renderActions : undefined}
				renderHeadActions={onAction ? renderHeadActions : undefined}
				hideHead={hideHead}
				expandSignal={expandSignal}
				collapseSignal={collapseSignal}
				onEmptyAction={onEmptyCreate}
				disableExpandIcons={disableExpandIcons}
				focusId={focusId}
				focusSignal={focusSignal}
				highlightId={highlightId}
				highlightSignal={highlightSignal}
				disablePagination={disablePagination}
				parentId={parentId}
				emptyRowsFallBackContextType={emptyRowsFallBackContextType}
			/>
		</>
	)
}

export default EditableTable
